"use client"

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react"
import { createPortal } from "react-dom"
import { useTranslations } from "@/lib/intl/use-translations"
import { useAiAnswer, type AiTurn } from "@/components/chat/use-ai-answer"
import { continueInChatSeed } from "@/components/chat/continue-in-chat-seed"
import { useChatSession } from "@/components/chat/use-chat-session"
import { useChatLauncherContext } from "@/components/chat/chat-launcher-context"
import { ChatMessage } from "@/components/chat/chat-message"
import { EscalationAction } from "@/components/chat/escalation-action"
import { classifyIntent } from "@/lib/search/classify-intent"
import { hasAiPreviewActive } from "@/lib/ai-preview"
import { track } from "@/lib/analytics"

export interface AiAnswerLaneProps {
  scope: string
  country: string
  locale: string
  pageUrl: string
  pageTitle: string
  surface: "modal" | "search-page" | "listing" | "detail"
  envEnabled: boolean
  /** Controlled query from the host's single input (modal/listing). Omit on
   *  card surfaces (detail/search-page) where the lane renders its own input. */
  query?: string
  /** Host increments this on Enter to submit the current `query` (controlled). */
  submitNonce?: number
  /** Known index title tokens for intent (lowercased). Optional. */
  knownTokens?: ReadonlySet<string>
  /** Portal target id in a hero (detail/LP). When set, the lane renders via
   *  createPortal into `#<anchor>`. */
  inlineChip?: { anchor?: string }
}

/** Collapsed answer height (px) before "Show more" reveals the rest. ~3 lines —
 *  enough to read the gist without shoving the search results below the fold. */
const COLLAPSED_MAX = 116

/** Map a streaming AiTurn → ChatMessage props. `thinkingLabel` overrides the
 *  default "Thinking…" with the live progress line while the bot works. */
function turnMessage(
  turn: AiTurn,
  key: number,
  thinkingLabel?: string,
  sourcesLabel?: string
): React.ReactNode {
  return (
    <ChatMessage
      key={turn.messageId ?? `${turn.role}-${key}`}
      role={turn.role}
      content={turn.content}
      isStreaming={turn.isStreaming}
      variant="bare"
      thinkingLabel={thinkingLabel}
      sources={turn.sources}
      sourcesLabel={sourcesLabel}
    />
  )
}

export function AiAnswerLane(props: AiAnswerLaneProps): React.ReactNode {
  const {
    scope,
    country,
    locale,
    pageUrl,
    pageTitle,
    surface,
    envEnabled,
    query,
    submitNonce,
    knownTokens,
    inlineChip,
  } = props

  const t = useTranslations("chat")
  const controlled = query !== undefined

  // ── Preview gate ──
  const [enabled, setEnabled] = useState(envEnabled)
  useEffect(() => {
    if (!envEnabled && hasAiPreviewActive()) setEnabled(true)
  }, [envEnabled])

  const { turns, isStreaming, error, status, handedOff, ask } = useAiAnswer({
    scope,
    country,
    locale,
    pageUrl,
    pageTitle,
  })

  // Transient progress line for the in-flight bot turn. Falls back to the
  // ChatMessage default ("Thinking…") when no progress event has arrived.
  const statusLabel = ((): string | undefined => {
    if (!status) return undefined
    if (
      status.phase === "searching" &&
      status.index != null &&
      status.total != null
    ) {
      return t("status.searching", {
        index: String(status.index),
        total: String(status.total),
      })
    }
    // A multi-topic scan without known bounds reads the same as a single
    // lookup.
    if (status.phase === "searching") return t("status.retrieving")
    if (status.phase === "understanding") return t("status.understanding")
    return t("status.retrieving")
  })()
  const { setPanelOpen } = useChatSession({ country, locale })
  const { requestPanelOpen } = useChatLauncherContext()

  // Opening the global Chatwoot launcher. `requestPanelOpen()` is the live
  // signal the mounted ContextualChat watches (it opens on THIS page view);
  // persisting `panel_open` keeps it open across a remount. Only consulted in
  // chatwoot backend mode.
  //
  // The embed runs its own session, so it's seeded with the visitor's last
  // question rather than opened blank on a conversation it can't see
  // (AHD-13379, see `continueInChatSeed`).
  const openLauncher = useCallback(() => {
    setPanelOpen(true)
    requestPanelOpen(continueInChatSeed(turns, scope, handedOff))
  }, [setPanelOpen, requestPanelOpen, turns, scope, handedOff])

  // Intent for the controlled query.
  const intent = controlled
    ? classifyIntent(query ?? "", knownTokens ?? new Set())
    : "question"

  const lastUserMessage = (() => {
    for (let i = turns.length - 1; i >= 0; i--) {
      if (turns[i].role === "user") return turns[i].content
    }
    return ""
  })()

  // ── Progressive disclosure (controlled surfaces only) ──
  // On listing/modal/search-page the answer competes with live results below,
  // so it stays clamped to COLLAPSED_MAX until the user expands. Card surfaces
  // (detail/LP hero) are dedicated to the chat, so they render in full.
  const clampable = controlled
  const [expanded, setExpanded] = useState(false)
  const [overflowing, setOverflowing] = useState(false)
  const answerContentRef = useRef<HTMLDivElement>(null)
  const collapsed = clampable && !expanded

  useEffect(() => {
    if (!clampable) return
    const el = answerContentRef.current
    if (!el) return
    // Measure the natural content height (the ref sits *inside* the clamp, so
    // it isn't itself constrained) against the collapsed ceiling. +1 absorbs
    // sub-pixel rounding without swallowing a genuinely-clipped line.
    setOverflowing(el.scrollHeight > COLLAPSED_MAX + 1)
  }, [clampable, turns, expanded])

  // ── Stick-to-bottom while the answer streams ──
  // The typewriter reveal keeps growing the answer; pin the latest text in view
  // so the user doesn't have to scroll down to follow it — unless they've
  // scrolled up to read earlier content, in which case we leave them be.
  const scrollRef = useRef<HTMLDivElement>(null)
  const stickToBottomRef = useRef(true)
  const onScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    stickToBottomRef.current = distanceFromBottom < 24
  }, [])
  useEffect(() => {
    if (collapsed) return // clamped teaser doesn't scroll
    const el = scrollRef.current
    if (!el || !stickToBottomRef.current) return
    el.scrollTop = el.scrollHeight
  }, [turns, collapsed])

  // ── Submit ──
  const submit = useCallback(
    (message: string) => {
      const trimmed = message.trim()
      if (!trimmed) return
      const msgIntent = controlled
        ? classifyIntent(trimmed, knownTokens ?? new Set())
        : "question"
      track.aiAnswerRequested({
        surface,
        scope,
        intent: msgIntent,
        query_length: trimmed.length,
      })
      // Explicitly asking ("Ask Ella" / Enter) is a commitment to read the
      // answer — open the full panel rather than the clamped "Show more" teaser
      // (which is for an auto-offered answer competing with results below).
      setExpanded(true)
      void ask(trimmed)
    },
    [controlled, knownTokens, surface, scope, ask]
  )

  // Controlled submit on nonce bump (host pressed Enter). nav requires an
  // explicit click on the row, so the nonce never auto-submits nav intents.
  const lastNonce = useRef(submitNonce)
  useEffect(() => {
    if (submitNonce === lastNonce.current) return
    lastNonce.current = submitNonce
    const q = (query ?? "").trim()
    // Enter is an explicit ask — submit for any intent (incl. nav). Live typing
    // never auto-fires; only a deliberate Enter or click does. The subtle nav
    // click-row still covers the case where the user *doesn't* press Enter.
    if (q) submit(q)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitNonce])

  // ── Offer analytics (dedupe per distinct query) ──
  const offeredFor = useRef<string | null>(null)
  useEffect(() => {
    if (!controlled) return
    const q = (query ?? "").trim()
    if (!q || turns.length > 0) {
      if (!q) offeredFor.current = null
      return
    }
    if (offeredFor.current === q) return
    offeredFor.current = q
    track.aiAnswerOffered({
      surface,
      scope,
      intent,
      query_length: q.length,
    })
  }, [controlled, query, turns.length, surface, scope, intent])

  // ── Own-input state (card mode + follow-up) ──
  const [draft, setDraft] = useState("")
  const draftRef = useRef<HTMLTextAreaElement>(null)
  // Grow the textarea with its content (capped), so a pasted list of biomarkers
  // is fully visible. Runs after every draft change, including reset to "".
  const autosize = useCallback(() => {
    const el = draftRef.current
    if (!el) return
    el.style.height = "0px"
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`
  }, [])
  useEffect(autosize, [draft, autosize])
  const submitDraft = useCallback(() => {
    submit(draft)
    setDraft("")
  }, [submit, draft])
  const onDraftKey = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      // Enter submits; Shift+Enter inserts a newline so a multi-line question
      // (e.g. a list of biomarkers) can be typed or pasted.
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault()
        submitDraft()
      }
    },
    [submitDraft]
  )

  // ── Portal target ──
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null)
  useEffect(() => {
    if (!inlineChip?.anchor) return
    const desktop = document.getElementById(inlineChip.anchor)
    const mobile = document.getElementById(`${inlineChip.anchor}-mobile`)
    const pick = (el: HTMLElement | null) =>
      el && el.offsetParent !== null ? el : null
    setPortalTarget(pick(desktop) ?? pick(mobile) ?? desktop)
  }, [inlineChip?.anchor])

  if (!enabled) return null
  // DE (checkup phase): the "Ask Ella" AI product-advisor lane isn't offered in
  // Germany yet — hide it on every surface (listing / modal / search page /
  // detail hero) rather than per mount point. AHD-6680.
  // country-gate-ignore: DE checkup-phase content hide, not a page gate
  if (country === "de") return null

  const hasTurns = turns.length > 0
  const q = (query ?? "").trim()

  // Controlled with nothing typed and no conversation yet → render nothing.
  // The host's own search input is the entry point; no empty card needed.
  if (controlled && !q && !hasTurns) return null

  const sendIcon = (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  )

  const dot = (
    <span
      aria-hidden="true"
      className="block h-[11px] w-[11px] shrink-0 rounded-full"
      style={{
        background:
          "radial-gradient(circle at 32% 30%, #cd9ed4, #613862 70%, #371938)",
      }}
    />
  )

  const ownInput = (
    <div className="mt-3 flex items-end gap-1.5">
      <textarea
        ref={draftRef}
        rows={1}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onDraftKey}
        placeholder={t("assistant.placeholder")}
        aria-label={t("assistant.inputAria")}
        className="type-body-md text-aubergine-900 focus:border-aubergine-500 max-h-40 min-h-[42px] flex-1 resize-none rounded-[11px] border border-neutral-300 bg-white px-3.5 py-2.5 outline-none placeholder:text-neutral-400"
      />
      <button
        type="button"
        aria-label={t("assistant.ask")}
        onClick={submitDraft}
        className="bg-aubergine-900 hover:bg-aubergine-700 flex h-[42px] w-[42px] shrink-0 cursor-pointer items-center justify-center rounded-[11px] text-white transition-colors duration-150 active:scale-[0.97]"
      >
        {sendIcon}
      </button>
    </div>
  )

  const header = (
    <div>
      <div className="flex items-center gap-2">
        {dot}
        <h3 className="type-title-md m-0 font-semibold tracking-tight text-neutral-900">
          {t("assistant.title")}
        </h3>
      </div>
      <p className="type-body-sm mt-1 leading-snug text-neutral-500">
        {t("assistant.subtitle")}
      </p>
    </div>
  )

  let body: React.ReactNode

  if (hasTurns) {
    // ── Conversation ──
    body = (
      <div className="flex flex-col">
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className={
            collapsed
              ? "overflow-hidden"
              : "flex max-h-[340px] flex-col overflow-y-auto pr-0.5"
          }
          style={
            collapsed
              ? {
                  maxHeight: COLLAPSED_MAX,
                  WebkitMaskImage:
                    "linear-gradient(180deg, #000 64%, transparent 100%)",
                  maskImage:
                    "linear-gradient(180deg, #000 64%, transparent 100%)",
                }
              : undefined
          }
        >
          <div ref={answerContentRef} className="flex flex-col gap-2">
            {turns.map((turn, i) =>
              turnMessage(
                turn,
                i,
                turn.isStreaming ? statusLabel : undefined,
                t("ui.sources")
              )
            )}
          </div>
        </div>

        {error && (
          <button
            type="button"
            onClick={() => lastUserMessage && submit(lastUserMessage)}
            className="type-body-md mt-2 self-start font-medium text-neutral-900 underline underline-offset-4"
          >
            {t("assistant.retry")}
          </button>
        )}

        {clampable && overflowing && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="type-body-md mt-2 self-start font-semibold text-neutral-900 underline underline-offset-4"
          >
            {expanded ? t("assistant.showLess") : t("assistant.showMore")}
          </button>
        )}

        {/* A human owns the conversation now, and this lane has no live
            subscription: say so, and hand over to the embed instead of taking
            more questions whose replies would land where the visitor can't
            see them (AHD-13379). */}
        {handedOff && (
          <div className="mt-3 rounded-[11px] bg-neutral-50 px-3.5 py-2.5">
            <p className="type-body-sm text-neutral-600">
              {t("ui.connecting_team")}
            </p>
            <div className="mt-2">
              <EscalationAction
                surface={surface}
                scope={scope}
                onContinueInChat={openLauncher}
              />
            </div>
          </div>
        )}

        {!handedOff && (!clampable || expanded) && (
          <>
            {ownInput}
            <div className="mt-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
              <EscalationAction
                surface={surface}
                scope={scope}
                onContinueInChat={openLauncher}
              />
              <span className="type-body-sm text-neutral-400">
                {t("assistant.disclaimer")}
              </span>
            </div>
          </>
        )}
      </div>
    )
  } else if (!controlled) {
    // ── Card entry (detail / LP hero) — no live results to compete with ──
    body = (
      <div>
        {header}
        {ownInput}
      </div>
    )
  } else if (intent === "nav") {
    // ── Controlled, nav intent — keep the AI offer subtle ──
    body = (
      <button
        type="button"
        onClick={() => submit(q)}
        className="type-body-md flex w-full items-center gap-2.5 rounded-[11px] px-1 py-1 text-left font-medium text-neutral-700 transition-colors hover:bg-neutral-900/5"
      >
        {dot}
        {t("assistant.askAbout", { query: q })}
      </button>
    )
  } else {
    // ── Controlled, question/list intent — slim "Ask Ella" bar ──
    body = (
      <div className="flex items-center gap-3">
        {dot}
        <span className="type-body-md flex-1 truncate text-neutral-700">
          {t("assistant.askAbout", { query: q })}
        </span>
        <button
          type="button"
          onClick={() => submit(q)}
          className="bg-aubergine-900 hover:bg-aubergine-700 type-title-sm shrink-0 cursor-pointer rounded-[11px] px-4 py-2 font-medium text-white transition-colors duration-150 active:scale-[0.98]"
        >
          {t("assistant.ask")}
        </button>
      </div>
    )
  }

  const lane = (
    <section
      className="rounded-[16px] border border-neutral-200 bg-white p-3 sm:p-3.5"
      aria-label={t("assistant.title")}
    >
      {body}
      {isStreaming && (
        <span className="sr-only" role="status">
          {statusLabel ?? t("assistant.subtitle")}
        </span>
      )}
    </section>
  )

  if (inlineChip?.anchor) {
    if (!mounted || !portalTarget) return null
    return createPortal(lane, portalTarget)
  }

  return lane
}
